import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from 'components/core';
import Typho from '../../../core/OldComponents/Typho/Typho';
import { useGetUserQuery } from 'services/userApi';
import {
  VendorPayment,
  useApproveVendorPaymentsMutation,
  useGetApprovalScheduleQuery,
  useListVendorPaymentsQuery,
  useRejectVendorPaymentsMutation,
} from 'services/vendorPaymentsApi';
import PaymentDetailModal from './PaymentDetailModal';
import RejectReasonModal from './RejectReasonModal';

const currency = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});
const fmtAmount = (n: number | string | null | undefined) => {
  if (n === null || n === undefined) return '—';
  const v = typeof n === 'string' ? Number(n) : n;
  return Number.isFinite(v) ? currency.format(v) : '—';
};

const bankLast4Display = (row: VendorPayment): string => {
  const n = row.source_metadata?._sourceBankAccountNumber;
  if (typeof n === 'string' && n.length >= 4) return `•••• ${n.slice(-4)}`;
  return '—';
};

const DATE_COLUMN_BY_STATUS: Record<string, { label: string; field: keyof VendorPayment }> = {
  pending_approval: { label: 'Synced', field: 'created_at' },
  approved: { label: 'Approved', field: 'approved_at' },
  rejected: { label: 'Rejected', field: 'rejected_at' },
  voided: { label: 'Voided', field: 'voided_at' },
  filed: { label: 'Processed', field: 'filed_at' },
};
const defaultDateColumn = { label: 'Synced', field: 'created_at' as const };

const dateColumnFor = (statusFilter: string) =>
  DATE_COLUMN_BY_STATUS[statusFilter] ?? defaultDateColumn;

const fmtDate = (value: unknown): string => {
  if (!value || typeof value !== 'string') return '—';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
};

const approverName = (a: { first_name?: string; last_name?: string; email: string }): string => {
  const name = [a.first_name, a.last_name].filter(Boolean).join(' ').trim();
  return name || a.email;
};

const APPROVER_COLUMN_BY_STATUS: Record<
  string,
  { label: string; render: (row: VendorPayment) => string }
> = {
  pending_approval: {
    label: 'Assigned approvers',
    render: (row) => (row.assignedApprovers ?? []).map(approverName).join(', ') || '—',
  },
  approved: {
    label: 'Approved by',
    render: (row) => (row.approvedByUser ? approverName(row.approvedByUser) : '—'),
  },
  rejected: {
    label: 'Rejected by',
    render: (row) => (row.rejectedByUser ? approverName(row.rejectedByUser) : '—'),
  },
};
const defaultApproverColumn = {
  label: 'Assigned approvers',
  render: (row: VendorPayment) => (row.assignedApprovers ?? []).map(approverName).join(', ') || '—',
};

const approverColumnFor = (statusFilter: string) =>
  APPROVER_COLUMN_BY_STATUS[statusFilter] ?? defaultApproverColumn;

const STATUS_FILTERS = [
  { label: 'Pending', value: 'pending_approval' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
  { label: 'Voided', value: 'voided' },
  { label: 'Processed', value: 'filed' },
  { label: 'All', value: '' },
];

const statusDisplayLabel = (status: string): string => {
  if (status === 'filed') return 'Processed';
  return status.replace(/_/g, ' ');
};

const statusChipClass = (status: string) => {
  switch (status) {
    case 'pending_approval':
      return 'bg-yellow-100 text-yellow-800';
    case 'approved':
    case 'filed':
    case 'paid':
    case 'partially_paid':
      return 'bg-green-100 text-green-800';
    case 'rejected':
    case 'cancelled':
    case 'voided':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-grey-100 text-grey-600';
  }
};

const Queue = () => {
  const { data: user } = useGetUserQuery({});
  const userId = user?.id;
  const canActOnRow = (row: VendorPayment) =>
    !!userId &&
    row.status === 'pending_approval' &&
    (row.assigned_approver_user_ids ?? []).includes(userId);

  const [statusFilter, setStatusFilter] = useState<string>('pending_approval');
  const {
    data: rows,
    isLoading,
    isError,
    refetch,
  } = useListVendorPaymentsQuery({
    status: statusFilter || undefined,
    limit: 200,
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [detailRowId, setDetailRowId] = useState<string | null>(null);
  const [rejectTargetIds, setRejectTargetIds] = useState<string[] | null>(null);

  const [approveRows, approveState] = useApproveVendorPaymentsMutation();
  const [rejectRows, rejectState] = useRejectVendorPaymentsMutation();
  const isMutating = approveState.isLoading || rejectState.isLoading;

  const actionableIds = useMemo(
    () => (rows ?? []).filter((r) => canActOnRow(r)).map((r) => r.id),
    [rows, userId]
  );
  const allSelected = actionableIds.length > 0 && actionableIds.every((id) => selectedIds.has(id));

  const toggleRow = (id: string) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelectedIds(next);
  };
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(actionableIds));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const approveSelected = async () => {
    if (selectedIds.size === 0) return;
    const res = await approveRows(Array.from(selectedIds)).unwrap();
    // eslint-disable-next-line no-alert
    if (res.failed.length > 0) {
      window.alert(
        `${res.ok.length} approved, ${res.failed.length} failed:\n` +
          res.failed.map((f) => `• ${f.id}: ${f.error}`).join('\n')
      );
    }
    clearSelection();
    refetch();
  };

  const openRejectModal = () => {
    if (selectedIds.size === 0) return;
    setRejectTargetIds(Array.from(selectedIds));
  };

  const submitRejection = async (reason: string) => {
    if (!rejectTargetIds) return;
    const res = await rejectRows({
      ids: rejectTargetIds,
      rejection_reason: reason,
    }).unwrap();
    // eslint-disable-next-line no-alert
    if (res.failed.length > 0) {
      window.alert(
        `${res.ok.length} rejected, ${res.failed.length} failed:\n` +
          res.failed.map((f) => `• ${f.id}: ${f.error}`).join('\n')
      );
    }
    setRejectTargetIds(null);
    clearSelection();
    refetch();
  };

  const pendingSelected = useMemo(() => {
    if (!rows) return 0;
    return rows.filter((r) => selectedIds.has(r.id) && canActOnRow(r)).length;
  }, [rows, selectedIds, userId]);

  const dateColumn = dateColumnFor(statusFilter);
  const approverColumn = approverColumnFor(statusFilter);
  const { data: schedule } = useGetApprovalScheduleQuery();

  return (
    <div className="flex flex-col w-full mb-8">
      <div className="flex items-start justify-between w-full max-w-[1280px] mb-4">
        <Typho size="14s" type="p" color="black" weight="light">
          Payments synced from ResMan waiting for approval. Rules assign each payment to one or more
          admins; any assigned admin can approve. Approvals unblock the filing job that will charge
          the customer&rsquo;s bank.
        </Typho>
      </div>

      <div className="flex items-center justify-between w-full max-w-[1280px] mb-3 gap-3">
        <div className="border border-solid border-grey-100 flex rounded-lg overflow-hidden items-center justify-center h-[30px]">
          {STATUS_FILTERS.map((s, index) => {
            const isActive = statusFilter === s.value;
            const leftBorder = index === 0 ? 'border-l-0' : 'border-l border-solid border-grey-100';
            return (
              <button
                key={s.value || 'all'}
                type="button"
                style={{
                  color: isActive ? '#FFFFFF' : '#606266',
                  background: isActive ? '#0B1848' : '#FFF',
                }}
                className={`text-14s font-bold px-2 h-[30px] inline-flex items-center justify-center min-w-[98px] ${leftBorder}`}
                onClick={() => {
                  setStatusFilter(s.value);
                  clearSelection();
                }}
              >
                {s.label}
              </button>
            );
          })}
        </div>

        {selectedIds.size > 0 && (
          <div className="flex items-center gap-3">
            <span className="text-14s text-grey-600">
              {selectedIds.size} selected
              {pendingSelected !== selectedIds.size && ` (${pendingSelected} actionable)`}
            </span>
            <Button
              variants="defaultOutlined"
              buttonLabel="Reject"
              onClick={openRejectModal}
              isDisabled={isMutating || pendingSelected === 0}
              additionalClasses="px-4 py-2"
            />
            <Button
              variants="default"
              buttonLabel="Approve"
              onClick={approveSelected}
              isDisabled={isMutating || pendingSelected === 0}
              additionalClasses="px-4 py-2"
            />
          </div>
        )}
      </div>

      {schedule && (statusFilter === 'pending_approval' || statusFilter === 'approved') && (
        <div className="w-full max-w-[1280px] mb-3 text-12s text-grey-600">
          {statusFilter === 'pending_approval' && (
            <>
              {schedule.syncDescription}. {schedule.filingDescription}.
            </>
          )}
          {statusFilter === 'approved' && <>{schedule.filingDescription}.</>}
        </div>
      )}

      <div className="w-full max-w-[1280px]">
        {isLoading && <Typho type="p">Loading…</Typho>}
        {isError && (
          <Typho type="p" color="red">
            Failed to load vendor payments.
          </Typho>
        )}
        {!isLoading && rows && rows.length === 0 && (
          <Typho type="p" color="grey">
            No payments match the current filter.
          </Typho>
        )}
        {!isLoading && rows && rows.length > 0 && (
          <table className="w-full text-14s border border-grey-100 rounded-lg">
            <thead className="bg-grey-50 text-left">
              <tr>
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    disabled={actionableIds.length === 0}
                    className="w-4 h-4 accent-primary-500 disabled:opacity-40"
                  />
                </th>
                <th className="px-4 py-3 font-medium">Vendor</th>
                <th className="px-4 py-3 font-medium">Property</th>
                <th className="px-4 py-3 font-medium">Bank</th>
                <th className="px-4 py-3 font-medium text-right">Amount</th>
                <th className="px-4 py-3 font-medium">Approval rule</th>
                <th className="px-4 py-3 font-medium">{approverColumn.label}</th>
                <th className="px-4 py-3 font-medium">{dateColumn.label}</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <RowDisplay
                  key={row.id}
                  row={row}
                  canAct={canActOnRow(row)}
                  isSelected={selectedIds.has(row.id)}
                  onToggle={() => toggleRow(row.id)}
                  onOpenDetail={() => setDetailRowId(row.id)}
                  dateField={dateColumn.field}
                  renderApprover={approverColumn.render}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PaymentDetailModal
        rowId={detailRowId}
        onClose={() => setDetailRowId(null)}
        onMutationDone={() => {
          setDetailRowId(null);
          refetch();
        }}
      />
      <RejectReasonModal
        isOpen={rejectTargetIds !== null}
        count={rejectTargetIds?.length ?? 0}
        onClose={() => setRejectTargetIds(null)}
        onSubmit={submitRejection}
        isSubmitting={rejectState.isLoading}
      />
    </div>
  );
};

const RowDisplay = ({
  row,
  canAct,
  isSelected,
  onToggle,
  onOpenDetail,
  dateField,
  renderApprover,
}: {
  row: VendorPayment;
  canAct: boolean;
  isSelected: boolean;
  onToggle: () => void;
  onOpenDetail: () => void;
  dateField: keyof VendorPayment;
  renderApprover: (row: VendorPayment) => string;
}) => {
  return (
    <tr className="border-t border-grey-100 hover:bg-grey-50">
      <td className="px-3 py-3 w-10">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggle}
          disabled={!canAct}
          className="w-4 h-4 accent-primary-500 disabled:opacity-40"
        />
      </td>
      <td className="px-4 py-3 cursor-pointer" onClick={onOpenDetail}>
        <div className="font-medium text-primary-500 hover:underline">
          {row.source_metadata?.vendorName ?? row.source_metadata?.vendorAbbreviation ?? '—'}
        </div>
      </td>
      <td className="px-4 py-3">
        {row.property?.id ? (
          <Link
            to={`/properties/payment-batches/${row.property.id}`}
            className="text-primary-500 hover:underline"
          >
            {row.property?.name ?? '—'}
          </Link>
        ) : (
          '—'
        )}
      </td>
      <td className="px-4 py-3 text-grey-600">{bankLast4Display(row)}</td>
      <td className="px-4 py-3 text-right">{fmtAmount(row.amount)}</td>
      <td className="px-4 py-3">{row.matchedRule?.approver_role_label ?? '—'}</td>
      <td className="px-4 py-3 text-grey-600">{renderApprover(row)}</td>
      <td className="px-4 py-3 text-grey-600">{fmtDate(row[dateField])}</td>
      <td className="px-4 py-3">
        <span className={`px-2 py-1 rounded text-12s ${statusChipClass(row.status)}`}>
          {statusDisplayLabel(row.status)}
        </span>
      </td>
    </tr>
  );
};

export default Queue;
